/**
 * Sync #app-shell / #game-host and fixed overlays to the browser visual viewport.
 * See `.cursor/rules/game-viewport-layout.mdc` before changing shell/overlay layout.
 */

import { MOBILE_NPC_OVERLAY_BREAKPOINT } from "../game/viewport-breakpoint.js";

export const VV_CSS_TOP = "--ccc-vv-top";
export const VV_CSS_LEFT = "--ccc-vv-left";
export const VV_CSS_WIDTH = "--ccc-vv-width";
export const VV_CSS_HEIGHT = "--ccc-vv-height";

export const IN_GAME_SHELL_CLASS = "ccc-in-game";

const MIN_HOST_WIDTH = 320;
const MIN_HOST_HEIGHT = 240;

export type VisualViewportRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

let boundsSynced = false;

/** True while `bindVisualViewportSync` is active (in-game). */
export function isVisualViewportBoundsSynced(): boolean {
  return boundsSynced;
}

/** Visible viewport rect; falls back to layout viewport when API is missing. */
export function readVisualViewportRect(win: Window): VisualViewportRect {
  const vv = win.visualViewport;
  if (vv) {
    return clampVisualViewportRect(
      {
        top: Math.round(vv.offsetTop),
        left: Math.round(vv.offsetLeft),
        width: Math.round(vv.width),
        height: Math.round(vv.height),
      },
      win
    );
  }
  return {
    top: 0,
    left: 0,
    width: Math.max(MIN_HOST_WIDTH, Math.round(win.innerWidth)),
    height: Math.max(MIN_HOST_HEIGHT, Math.round(win.innerHeight)),
  };
}

export function clampVisualViewportRect(rect: VisualViewportRect, win: Window): VisualViewportRect {
  const fallbackW = Math.max(MIN_HOST_WIDTH, Math.round(win.innerWidth));
  const fallbackH = Math.max(MIN_HOST_HEIGHT, Math.round(win.innerHeight));
  const width = rect.width >= MIN_HOST_WIDTH ? rect.width : fallbackW;
  const height = rect.height >= MIN_HOST_HEIGHT ? rect.height : fallbackH;
  return {
    top: Math.max(0, rect.top),
    left: Math.max(0, rect.left),
    width,
    height,
  };
}

/** Lock #app-shell to visual viewport on narrow viewports (mobile browser chrome). */
export function shouldFixAppShellToVisualViewport(vw: number): boolean {
  return vw < MOBILE_NPC_OVERLAY_BREAKPOINT;
}

/** @deprecated Use shouldFixAppShellToVisualViewport */
export const shouldFixGameHostToVisualViewport = shouldFixAppShellToVisualViewport;

export function applyVisualViewportCssVars(doc: Document, rect: VisualViewportRect): void {
  const root = doc.documentElement;
  root.style.setProperty(VV_CSS_TOP, `${rect.top}px`);
  root.style.setProperty(VV_CSS_LEFT, `${rect.left}px`);
  root.style.setProperty(VV_CSS_WIDTH, `${rect.width}px`);
  root.style.setProperty(VV_CSS_HEIGHT, `${rect.height}px`);
}

export function clearVisualViewportCssVars(doc: Document): void {
  const root = doc.documentElement;
  for (const key of [VV_CSS_TOP, VV_CSS_LEFT, VV_CSS_WIDTH, VV_CSS_HEIGHT]) {
    root.style.removeProperty(key);
  }
}

export function applyInGameViewportShell(shell: HTMLElement, _rect: VisualViewportRect): void {
  shell.classList.add(IN_GAME_SHELL_CLASS);
}

export function resetInGameViewportShell(shell: HTMLElement): void {
  shell.classList.remove(IN_GAME_SHELL_CLASS);
}

/** Document offset of #game-host for HTML UI anchored to Phaser HUD coordinates. */
export function readGameHostDocumentOffset(gameHost: HTMLElement): { top: number; left: number } {
  const box = gameHost.getBoundingClientRect();
  return { top: Math.round(box.top), left: Math.round(box.left) };
}

/** Size Phaser should use — laid-out #game-host box inside the viewport shell. */
export function readGameHostLayoutRect(gameHost: HTMLElement, win: Window): VisualViewportRect {
  const box = gameHost.getBoundingClientRect();
  if (box.width >= MIN_HOST_WIDTH && box.height >= MIN_HOST_HEIGHT) {
    return {
      top: Math.round(box.top),
      left: Math.round(box.left),
      width: Math.round(box.width),
      height: Math.round(box.height),
    };
  }
  return readVisualViewportRect(win);
}

export type BindVisualViewportSyncOptions = {
  shell: HTMLElement;
  gameHost: HTMLElement;
  win?: Window;
  onResize: (rect: VisualViewportRect) => void;
};

let refreshVisualViewportSync: (() => void) | null = null;

/** Re-run viewport sync (e.g. when opening journal over the play area). */
export function syncVisualViewportBounds(): void {
  refreshVisualViewportSync?.();
}

export type VisualViewportUnbind = () => void;

/** CSS vars + in-game shell layout; returns unbind. */
export function bindVisualViewportSync(options: BindVisualViewportSyncOptions): VisualViewportUnbind {
  const win = options.win ?? window;
  const doc = win.document;

  const sync = (): void => {
    const vvRect = readVisualViewportRect(win);
    boundsSynced = true;
    applyVisualViewportCssVars(doc, vvRect);
    applyInGameViewportShell(options.shell, vvRect);
    options.onResize(readGameHostLayoutRect(options.gameHost, win));
  };

  refreshVisualViewportSync = sync;
  sync();

  const onVvChange = (): void => sync();
  const onWinResize = (): void => sync();

  win.visualViewport?.addEventListener("resize", onVvChange);
  win.visualViewport?.addEventListener("scroll", onVvChange);
  win.addEventListener("resize", onWinResize);

  return () => {
    win.visualViewport?.removeEventListener("resize", onVvChange);
    win.visualViewport?.removeEventListener("scroll", onVvChange);
    win.removeEventListener("resize", onWinResize);
    refreshVisualViewportSync = null;
    boundsSynced = false;
    clearVisualViewportCssVars(doc);
    resetInGameViewportShell(options.shell);
  };
}
